import { useCallback, useState } from 'react';
import { ExchangeableItem, SalesOrder } from '../../../../types/sales.types';
import { salesService } from '../../../../services/sales.service';

export function useExchangeEligibility(order: SalesOrder | null) {
  const [eligibility, setEligibility] = useState<ExchangeableItem[]>([]);
  const [isLoadingEligibility, setIsLoadingEligibility] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);

  const loadEligibility = useCallback(async () => {
    if (!order) return;

    try {
      setIsLoadingEligibility(true);
      setEligibilityError(null);

      const eligibilityResponse = await salesService.checkExchangeEligibility(order.id);

      const exchangeableItems: ExchangeableItem[] = eligibilityResponse.exchangeableItems.map(
        (item: {
          itemId: number;
          productName: string;
          productCode: string;
          originalPrice: number;
          finalPrice: number;
          canExchange: boolean;
          reason?: string;
        }) => {
          const orderItem = order.items.find((oi) => oi.id === item.itemId);
          return {
            itemId: item.itemId,
            orderItem: orderItem!,
            productName: item.productName,
            productCode: item.productCode,
            originalPrice: item.originalPrice,
            finalPrice: item.finalPrice,
            canExchange: item.canExchange,
            reason: item.reason,
          };
        },
      );

      setEligibility(exchangeableItems);

      if (!eligibilityResponse.isEligible && eligibilityResponse.ineligibilityReasons.length > 0) {
        setEligibilityError(eligibilityResponse.ineligibilityReasons.join('. '));
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error al cargar elegibilidad de intercambio';
      setEligibilityError(errorMessage);
      setEligibility(
        order.items.map((item) => ({
          itemId: item.id,
          orderItem: item,
          productName: item.warehouseInventoryProduct.product.name,
          productCode: item.warehouseInventoryProduct.productCodeUnique,
          originalPrice: item.originalPrice,
          finalPrice: item.finalPrice,
          canExchange: false,
          reason: errorMessage,
        })),
      );
    } finally {
      setIsLoadingEligibility(false);
    }
  }, [order]);

  const clearEligibilityError = useCallback(() => {
    setEligibilityError(null);
  }, []);

  return {
    eligibility,
    isLoadingEligibility,
    eligibilityError,
    loadEligibility,
    clearEligibilityError,
  };
}
